import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast, Toaster } from "sonner";
import { 
  Package, Search, Calendar, MapPin, User, Phone, 
  CheckCircle, Clock, Truck, XCircle, ShoppingCart,
  Filter, RefreshCw
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SupplyRequests = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [token]);

  useEffect(() => {
    filterRequests();
  }, [requests, searchQuery, statusFilter, categoryFilter]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/supply-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      toast.error("فشل في تحميل طلبات المستلزمات");
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.item_name?.toLowerCase().includes(query) ||
        r.location_name?.toLowerCase().includes(query) ||
        r.reporter_name?.toLowerCase().includes(query)
      );
    }
    
    if (statusFilter !== "all") {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    
    if (categoryFilter !== "all") {
      filtered = filtered.filter(r => r.category === categoryFilter);
    }
    
    setFilteredRequests(filtered);
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/supply-requests/${requestId}?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم تحديث الحالة بنجاح");
      fetchRequests();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error("فشل في تحديث الحالة");
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { label: "قيد الانتظار", color: "bg-yellow-100 text-yellow-700", icon: Clock },
      approved: { label: "تمت الموافقة", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
      ordered: { label: "تم الطلب", color: "bg-purple-100 text-purple-700", icon: ShoppingCart },
      delivered: { label: "تم التسليم", color: "bg-green-100 text-green-700", icon: Truck },
      rejected: { label: "مرفوض", color: "bg-red-100 text-red-700", icon: XCircle }
    };
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getCategoryLabel = (category) => {
    const labels = {
      medical: "مستلزمات طبية",
      office: "أدوات مكتبية",
      cleaning: "مواد تنظيف",
      electrical: "كهربائيات",
      other: "أخرى"
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      medical: "bg-red-100 text-red-700",
      office: "bg-blue-100 text-blue-700",
      cleaning: "bg-green-100 text-green-700",
      electrical: "bg-yellow-100 text-yellow-700",
      other: "bg-slate-100 text-slate-700"
    };
    return colors[category] || colors.other;
  };

  const getUrgencyBadge = (urgency) => {
    const configs = {
      urgent: { label: "عاجل", color: "bg-red-500 text-white" },
      normal: { label: "عادي", color: "bg-yellow-100 text-yellow-700" },
      low: { label: "منخفض", color: "bg-green-100 text-green-700" }
    };
    const config = configs[urgency] || configs.normal;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === "pending").length,
    approved: requests.filter(r => r.status === "approved").length,
    ordered: requests.filter(r => r.status === "ordered").length,
    delivered: requests.filter(r => r.status === "delivered").length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[#655dc6]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#4a3fa8]">طلبات المستلزمات</h1>
          <p className="text-slate-500">إدارة ومتابعة طلبات المستلزمات</p>
        </div>
        <Button onClick={fetchRequests} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          تحديث
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-[#655dc6]">{stats.total}</p>
            <p className="text-sm text-slate-500">إجمالي الطلبات</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-yellow-50">
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-sm text-yellow-700">قيد الانتظار</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.approved}</p>
            <p className="text-sm text-blue-700">تمت الموافقة</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-purple-50">
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.ordered}</p>
            <p className="text-sm text-purple-700">تم الطلب</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-green-50">
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
            <p className="text-sm text-green-700">تم التسليم</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="بحث بالصنف أو الموقع..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pr-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="الحالة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="pending">قيد الانتظار</SelectItem>
                <SelectItem value="approved">تمت الموافقة</SelectItem>
                <SelectItem value="ordered">تم الطلب</SelectItem>
                <SelectItem value="delivered">تم التسليم</SelectItem>
                <SelectItem value="rejected">مرفوض</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="التصنيف" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع التصنيفات</SelectItem>
                <SelectItem value="medical">مستلزمات طبية</SelectItem>
                <SelectItem value="office">أدوات مكتبية</SelectItem>
                <SelectItem value="cleaning">مواد تنظيف</SelectItem>
                <SelectItem value="electrical">كهربائيات</SelectItem>
                <SelectItem value="other">أخرى</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">لا توجد طلبات مستلزمات</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredRequests.map((request) => (
            <Card 
              key={request.id} 
              className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => { setSelectedRequest(request); setDetailDialogOpen(true); }}
            >
              <CardContent className="pt-4">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Package className="w-5 h-5 text-[#655dc6]" />
                      <h3 className="font-semibold text-lg">{request.item_name}</h3>
                      {getUrgencyBadge(request.urgency)}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {request.location_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(request.created_at).toLocaleDateString("ar-SA")}
                      </span>
                      <Badge className={getCategoryColor(request.category)}>
                        {getCategoryLabel(request.category)}
                      </Badge>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-slate-600">الكمية: </span>
                      <span className="font-medium">{request.quantity} {request.unit}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(request.status)}
                    <Button variant="outline" size="sm">
                      عرض التفاصيل
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#4a3fa8]">تفاصيل طلب المستلزمات</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <span className="text-slate-600">الحالة</span>
                {getStatusBadge(selectedRequest.status)}
              </div>

              {/* Item Info */}
              <div className="p-4 bg-[#F5F3F9] rounded-lg">
                <h3 className="font-bold text-lg text-[#4a3fa8] mb-2">{selectedRequest.item_name}</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">الكمية:</span>
                    <span className="font-medium mr-2">{selectedRequest.quantity} {selectedRequest.unit}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">التصنيف:</span>
                    <Badge className={`mr-2 ${getCategoryColor(selectedRequest.category)}`}>
                      {getCategoryLabel(selectedRequest.category)}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-slate-500">الأولوية:</span>
                    <span className="mr-2">{getUrgencyBadge(selectedRequest.urgency)}</span>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <MapPin className="w-5 h-5 text-[#655dc6]" />
                <div>
                  <p className="text-sm text-slate-500">الموقع</p>
                  <p className="font-medium">{selectedRequest.location_name}</p>
                </div>
              </div>

              {/* Reporter */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500 mb-2">معلومات الطالب</p>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#655dc6]" />
                    {selectedRequest.reporter_name}
                  </span>
                  {selectedRequest.reporter_whatsapp && (
                    <span className="flex items-center gap-2 text-green-600">
                      <Phone className="w-4 h-4" />
                      <span dir="ltr">{selectedRequest.reporter_whatsapp}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedRequest.notes && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">ملاحظات</p>
                  <p>{selectedRequest.notes}</p>
                </div>
              )}

              {/* Date */}
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Calendar className="w-4 h-4" />
                <span>تاريخ الطلب: {new Date(selectedRequest.created_at).toLocaleString("ar-SA")}</span>
              </div>

              {/* Action Buttons */}
              {selectedRequest.status !== "delivered" && selectedRequest.status !== "rejected" && (
                <div className="border-t pt-4 space-y-2">
                  <p className="text-sm text-slate-500 mb-2">تحديث الحالة:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedRequest.status === "pending" && (
                      <>
                        <Button 
                          onClick={() => updateStatus(selectedRequest.id, "approved")}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          <CheckCircle className="w-4 h-4 ml-2" />
                          موافقة
                        </Button>
                        <Button 
                          onClick={() => updateStatus(selectedRequest.id, "rejected")}
                          variant="destructive"
                          className="text-white"
                        >
                          <XCircle className="w-4 h-4 ml-2" />
                          رفض
                        </Button>
                      </>
                    )}
                    {selectedRequest.status === "approved" && (
                      <Button 
                        onClick={() => updateStatus(selectedRequest.id, "ordered")}
                        className="bg-purple-500 hover:bg-purple-600 text-white"
                      >
                        <ShoppingCart className="w-4 h-4 ml-2" />
                        تم الطلب
                      </Button>
                    )}
                    {selectedRequest.status === "ordered" && (
                      <Button 
                        onClick={() => updateStatus(selectedRequest.id, "delivered")}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <Truck className="w-4 h-4 ml-2" />
                        تم التسليم
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplyRequests;
